import React, { useState, useEffect } from 'react';
import { SPM6_DATA, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Sliders, Play, Pause, Activity, Zap } from 'lucide-react';

interface SPM6CumulativeProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SPM6Cumulative: React.FC<SPM6CumulativeProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm6'];
  const audioEngine = AudioEngine.getInstance();

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Carbon Metronome playback loop
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % SPM6_DATA.length;
          triggerCarbonPulse(next);
          return next;
        });
      }, 550);
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  const triggerCarbonPulse = (idx: number) => {
    if (!audioEngine.getIsUnlocked()) return;
    const pt = SPM6_DATA[idx];
    if (!pt) return;

    // Central pitch
    const centerFreq = audioEngine.tempToFreq(pt.tempCentral);

    // Trigger Carbon Metronome beat (Extreme-Transient patch)
    audioEngine.triggerExtremeTransient(centerFreq, 0, 0.6);

    // Model Uncertainty detune
    const lowerFreq = audioEngine.tempToFreq(pt.tempLower);
    const upperFreq = audioEngine.tempToFreq(pt.tempUpper);

    // Left channel lower bound hit, Right channel upper bound hit
    audioEngine.triggerExtremeTransient(lowerFreq, -0.6, 0.4);
    audioEngine.triggerExtremeTransient(upperFreq, 0.6, 0.4);
  };

  const activePt = SPM6_DATA[currentIndex];

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Metronome' : 'Play Carbon Metronome'}</span>
        </button>
      </div>

      {/* Main Interactive Metronome Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        {/* Active Stats Banner */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs text-slate-400 block font-mono">Cumulative CO2 Emissions</span>
            <span className="text-2xl font-extrabold font-mono text-cyan-400">
              {activePt.cumulativeCO2} <span className="text-xs text-slate-400 font-sans">GtCO2</span>
            </span>
            <span className="text-[10px] text-slate-500 block">Approx. Year ~{activePt.yearApprox}</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs text-slate-400 block font-mono">Central Warming (TCRE)</span>
            <span className="text-2xl font-extrabold font-mono text-amber-400">
              +{activePt.tempCentral.toFixed(2)}°C
            </span>
            <span className="text-[10px] text-slate-500 block">Pitch Anchor Center Tone</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs text-slate-400 block font-mono">Model Uncertainty Range</span>
            <span className="text-xl font-extrabold font-mono text-red-400">
              +{activePt.tempLower.toFixed(2)}°C &rarr; +{activePt.tempUpper.toFixed(2)}°C
            </span>
            <span className="text-[10px] text-slate-500 block">Stereo Panned Detune (Left / Right)</span>
          </div>
        </div>

        {/* Timeline Scrubber Slider */}
        <div className="space-y-2 pt-2">
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span>0 GtCO2 (1850)</span>
            <span className="text-cyan-300 font-bold">Selected: {activePt.cumulativeCO2} GtCO2</span>
            <span>4500 GtCO2 (2080)</span>
          </div>
          <input
            type="range"
            min="0"
            max={SPM6_DATA.length - 1}
            value={currentIndex}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              setCurrentIndex(val);
              triggerCarbonPulse(val);
            }}
            className="w-full h-2 rounded-lg bg-slate-800 accent-cyan-400 cursor-pointer"
          />
        </div>

        {/* Visual Pulse Metronome Wave */}
        <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-center gap-2">
          {SPM6_DATA.map((pt, idx) => {
            const isActive = idx === currentIndex;
            const heightPx = Math.max(16, (pt.tempCentral / 3.5) * 80);
            return (
              <div
                key={pt.cumulativeCO2}
                onClick={() => {
                  setCurrentIndex(idx);
                  triggerCarbonPulse(idx);
                }}
                className={`w-6 rounded-full transition-all duration-300 cursor-pointer ${
                  isActive
                    ? 'bg-gradient-to-t from-cyan-500 to-amber-400 glow-amber scale-110'
                    : 'bg-slate-800 hover:bg-slate-700'
                }`}
                style={{ height: `${heightPx}px` }}
                title={`${pt.cumulativeCO2} GtCO2 (+${pt.tempCentral.toFixed(2)}°C)`}
              ></div>
            );
          })}
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
